import React, { useContext, useEffect, useRef, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import Carousel from 'react-native-reanimated-carousel';
import FontAwesome6 from '@expo/vector-icons/FontAwesome6';
import PropTypes from 'prop-types';
import CdnImageBackground from '../common/CdnImageBackground';
import { H3, H5 } from '../text';
import LayoutContext from '../../contexts/Layout';
import ComponentTitle from '../common/ComponentTitle';
import Button from '../common/Button';
import Utils from '../../common/Utils';

const ImageCarouselItem = ({
    carouselRef,
    enableImageOverlay = false,
    followContentLink,
    description,
    disabled,
    image,
    layout,
    link,
    nodeTypeAlias = 'ImageCarouselItem',
    setCarouselHeight,
    title,
    width
}) => {
    const [imageDimensions, setImageDimensions] = useState();
    const hasLink = (link.isExternal && link.externalUrl) || !!link.internalKey;

    useEffect(() => {
        if (imageDimensions?.height) {
            setCarouselHeight(h => {
                if (imageDimensions.height !== h) {
                    return imageDimensions.height;
                }

                return h;
            });
        }
    }, [imageDimensions, setCarouselHeight]);

    return (
        <CdnImageBackground
            enableOverlay={enableImageOverlay}
            imageDimensions={imageDimensions}
            setImageDimensions={setImageDimensions}
            style={{ opacity: disabled ? 0.45 : 1 }}
            uri={image.cdnImagePath}
            width={width}
        >
            <View
                style={StyleSheet.flatten(Styles.contentContainer, {
                    height: imageDimensions?.height,
                    marginBottom: layout.styleGuide.spacer3,
                    marginHorizontal: layout.styleGuide.indent
                })}
                dataSet={{
                    umbracoComponentName: nodeTypeAlias,
                    spacerBottom: `StyleGuide.Spacer3(${layout.styleGuide.spacer3})`,
                    spacerLeft: `StyleGuide.Indent(${layout.styleGuide.indent})`,
                    spacerRight: `StyleGuide.Indent(${layout.styleGuide.indent})`
                }}
            >
                <View style={{ flex: 1 }} />
                {!layout.device.isMobile && (
                    <View style={Styles.navigationControls}>
                        <FontAwesome6 color='#fff' name='chevron-left' onPress={() => carouselRef.current?.next()} size={40} />
                        <FontAwesome6 color='#fff' name='chevron-right' onPress={() => carouselRef.current?.prev()} size={40} />
                    </View>
                )}
                <View style={layout.device.isMobile ? Styles.detailsContainer.mobile : Styles.detailsContainer}>
                    <View>
                        <H3
                            style={{ color: 'white', paddingBottom: 2.5, maxWidth: layout.device.os.equals('web') ? Math.floor(imageDimensions?.width * 0.6) : undefined }}
                            text={title}
                        />
                        <H5 style={Styles.detailsContainer.description} text={description} />
                    </View>
                    {hasLink && (
                        <Button
                            style={{ backgroundColor: 'transparent' }}
                            text='Watch Now'
                            onPress={async () => await followContentLink(link.isExternal, link.internalKey, link.externalUrl)}
                        />
                    )}
                </View>
            </View>
        </CdnImageBackground>
    );
};

const ImageCarousel = ({ containerRef, contentItems, followContentLink, heading, nodeTypeAlias = 'ImageCarousel', width, hardWidth = 12.345 }) => {
    const carouselRef = useRef();
    const { layout } = useContext(LayoutContext);
    const sharedValue = useSharedValue(0);
    const [activeIndex, setActiveIndex] = useState(0);
    const [carouselHeight, setCarouselHeight] = useState(Math.floor(hardWidth * 0.75));
    const items = Object.values(contentItems);

    return (
        <View
            style={{ marginBottom: layout.styleGuide.componentSeparator }}
            dataSet={{ umbracoComponentName: nodeTypeAlias, spacerBottom: `StyleGuide.ComponentSeparator(${layout.styleGuide.componentSeparator})` }}
        >
            <ComponentTitle title={heading} />
            <Carousel
                autoPlay={false}
                data={items}
                loop={true}
                mode='parallax'
                modeConfig={{
                    parallaxAdjacentItemScale: 0.55, // scale of items behind currently active item
                    //   parallaxScrollingScale: 0.8,
                    parallaxScrollingOffset: Math.floor(hardWidth * 0.45)
                }}
                onConfigurePanGesture={panGesture => {
                    // fix panGesture so that the carousel works correctly
                    // within our parent ScrollView
                    panGesture.config.touchAction = 'pan-y'; // for web

                    if (!Platform.OS.equals('web')) {
                        panGesture.activeOffsetX([-5, 5]);
                        panGesture.failOffsetY([-5, 5]);
                    }
                }}
                onProgressChange={(_, index) => {
                    sharedValue.value = index;
                    setActiveIndex(index);
                }}
                onScrollEnd={index => setActiveIndex(index)}
                pagingEnabled={false}
                ref={carouselRef}
                renderItem={({ item, index }) => {
                    const { key, rest } = Utils.splitKeyFromProps(item);

                    return (
                        <ImageCarouselItem
                            {...rest}
                            key={key}
                            carouselRef={carouselRef}
                            followContentLink={followContentLink}
                            disabled={index !== activeIndex}
                            index={index}
                            layout={layout}
                            setCarouselHeight={setCarouselHeight}
                            width={hardWidth}
                        />
                    );
                }}
                scrollAnimationDuration={250}
                snapEnabled={true}
                style={{
                    height: carouselHeight,
                    width: hardWidth
                }}
            />
            {items.length > 1 && layout.device.isMobile && (
                <View style={Styles.paginationContainer}>
                    {items.map((item, index) => {
                        return (
                            <View
                                key={item.id}
                                style={StyleSheet.flatten(Styles.paginationDot, {
                                    backgroundColor: index === activeIndex ? '#fffff' : '#dde4e9',
                                    borderColor: index === activeIndex ? '#153d6e' : null,
                                    borderWidth: index === activeIndex ? 1 : 0
                                })}
                            />
                        );
                    })}
                </View>
            )}
        </View>
    );
};

const Styles = StyleSheet.create({
    contentContainer: {
        backgroundColor: 'transparent',
        flex: 3,
        justifyContent: 'space-evenly'
    },
    detailsContainer: {
        flexDirection: 'row',
        flex: 1,
        justifyContent: 'space-between',
        maxHeight: '12%',

        description: {
            color: 'white',
            fontFamily: 'GothamBook',
            fontSize: Platform.OS.equals('web') ? '16pt' : 16,
            height: Platform.OS.equals('web') ? '18pt' : 18
        },

        actionButton: {
            alignItems: 'center',
            borderColor: '#fff',
            borderRadius: 5,
            borderWidth: 1,
            elevation: 3,
            justifyContent: 'center'
        },

        actionText: {
            color: '#fff',
            fontFamily: 'GothamBook',
            fontSize: Platform.OS.equals('web') ? '16pt' : 16,
            letterSpacing: 0.25,
            lineHeight: 28
        },

        mobile: {
            flex: 1
        }
    },
    navigationControls: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        flex: 1
    },
    paginationContainer: {
        alignItems: 'center',
        backgroundColor: 'white',
        flexDirection: 'row',
        height: 15,
        justifyContent: 'center'
    },
    paginationDot: {
        borderRadius: 25,
        height: 10,
        marginHorizontal: 2,
        width: 10
    }
});

ImageCarousel.propTypes = {
    contentItems: PropTypes.object,
    title: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default ImageCarousel;
